import React from 'react';
import { 
  Ban, Shield, Edit, Trash2, KeyRound, CheckCircle, 
  XCircle, Eye
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

const UserTable = ({
  users,
  loading,
  getRoleBadgeColor,
  onViewDetails,
  onEditUser,
  onResetPassword,
  onChangeRole,
  onSuspend,
  onReactivate,
  onDelete
}) => {
  if (loading) {
    return <div className="text-center py-20 text-gray-400">Loading users...</div>;
  }

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-x-auto">
      <table className="w-full min-w-[600px]">
        <thead className="bg-gray-800">
          <tr>
            <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">User</th>
            <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase hidden sm:table-cell">Email</th>
            <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Role</th>
            <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Status</th>
            <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-gray-800/50 transition-colors">
              <td className="px-3 md:px-4 py-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                    {(user.username || 'U')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-white text-sm truncate">{user.username}</p>
                    <p className="text-xs text-gray-500 truncate sm:hidden">{user.email}</p>
                  </div>
                </div>
              </td>
              <td className="px-3 md:px-4 py-3 text-gray-300 text-sm hidden sm:table-cell">{user.email}</td>
              <td className="px-3 md:px-4 py-3">
                <Badge className={`${getRoleBadgeColor(user.role)} text-white text-xs`}>
                  {user.role}
                </Badge>
              </td>
              <td className="px-3 md:px-4 py-3">
                {user.is_active ? (
                  <Badge className="bg-green-500/20 text-green-400 border border-green-500/30 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" /> Active
                  </Badge>
                ) : (
                  <Badge className="bg-red-500/20 text-red-400 border border-red-500/30 text-xs">
                    <XCircle className="w-3 h-3 mr-1" /> Suspended
                  </Badge>
                )}
              </td>
              <td className="px-3 md:px-4 py-3">
                <UserActions
                  user={user}
                  onViewDetails={onViewDetails}
                  onEditUser={onEditUser}
                  onResetPassword={onResetPassword}
                  onChangeRole={onChangeRole}
                  onSuspend={onSuspend}
                  onReactivate={onReactivate}
                  onDelete={onDelete}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UserActions = ({
  user,
  onViewDetails,
  onEditUser,
  onResetPassword,
  onChangeRole,
  onSuspend,
  onReactivate,
  onDelete
}) => {
  return (
    <div className="flex items-center justify-end gap-0.5 md:gap-1">
      <Button
        onClick={() => onViewDetails(user.id)}
        variant="ghost"
        size="sm"
        className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 p-1 md:p-2"
        title="View Full Account"
      >
        <Eye className="w-4 h-4" />
      </Button>
      
      {user.role !== 'admin' && (
        <>
          <Button
            onClick={() => onEditUser(user)}
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white hover:bg-gray-700 p-1 md:p-2 hidden md:flex"
            title="Edit User"
          >
            <Edit className="w-4 h-4" />
          </Button>
          
          <Button
            onClick={() => onResetPassword(user)}
            variant="ghost"
            size="sm"
            className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 p-1 md:p-2 hidden md:flex"
            title="Reset Password"
          >
            <KeyRound className="w-4 h-4" />
          </Button>
          
          <Button
            onClick={() => onChangeRole(user)}
            variant="ghost"
            size="sm"
            className="text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 p-1 md:p-2 hidden sm:flex"
            title="Change Role"
          >
            <Shield className="w-4 h-4" />
          </Button>

          {user.is_active ? (
            <Button
              onClick={() => onSuspend(user.id, user.username)}
              variant="ghost"
              size="sm"
              className="text-orange-400 hover:text-orange-300 hover:bg-orange-900/20 p-1 md:p-2"
              title="Suspend User"
            >
              <Ban className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={() => onReactivate(user.id, user.username)}
              variant="ghost"
              size="sm"
              className="text-green-400 hover:text-green-300 hover:bg-green-900/20 p-1 md:p-2"
              title="Reactivate User"
            >
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}

          <Button
            onClick={() => onDelete(user.id, user.username)}
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
            title="Delete User"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </>
      )}
    </div>
  );
};

export default UserTable;
